import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Link as MuiLink,
  Paper,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { api } from '@/utils/api';
import { formatCurrency } from '@/utils/helper';

import DonationCard from '../DonationCard';

/** @param {import("prop-types").InferProps<typeof StepReviewPropTypes>} props */
export default function StepReview({
  formData,
  prevStep,
  nextStep,
  setCurrentStep,
  shouldSaveInfo,
  setShouldSaveInfo,
}) {
  const { t, i18n } = useTranslation();
  const [bidwars, setBidwars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    const fetchBidwars = async () => {
      setLoading(true);
      try {
        const response = await api.get('/api/v2/bidwars/list/');
        setBidwars(response.data);
      } catch (error) {
        // eslint-disable-next-line no-console -- TODO: use an Alert
        console.error('Error fetching bidwars:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBidwars();
  }, []);

  const donationAmount =
    typeof formData.amount === 'number'
      ? formData.amount
      : Number.parseFloat(formData.amount || '0');

  const donationData = {
    ...formData,
    amount: donationAmount,
    createdAt: new Date().toISOString(),
  };

  const bidwarChoices = formData.bidwarChoices || {};
  const hasBidwarSelection = Object.values(bidwarChoices).some(
    choice => choice.optionId || choice.isOther,
  );

  const selectedBidwars = hasBidwarSelection
    ? Object.keys(bidwarChoices)
        .map(bidwarId => {
          const choice = bidwarChoices[bidwarId];
          if (!choice.optionId && !choice.isOther) return null;

          const bidwar = bidwars.find(b => b.id.toString() === bidwarId);
          if (!bidwar) return null;

          let optionLabel = '';
          if (choice.isOther) {
            optionLabel = `${t('donation.customChoice')}: ${choice.customValue}`;
          } else {
            const selectedOption = bidwar.options.find(option => option.id === choice.optionId);
            if (selectedOption) {
              optionLabel =
                i18n.language === 'fr' ? selectedOption.labelFr : selectedOption.labelEn;
            }
          }

          if (!optionLabel) return null;

          return {
            id: bidwar.id,
            bidwarLabel: i18n.language === 'fr' ? bidwar.labelFr : bidwar.labelEn,
            description: i18n.language === 'fr' ? bidwar.descriptionFr : bidwar.descriptionEn,
            optionLabel,
          };
        })
        .filter(Boolean)
    : [];

  return (
    <Box>
      <Typography gutterBottom variant="h5">
        {t('donation.stepReview')}
      </Typography>

      <Grid container spacing={3}>
        {/* Left side: Summary */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ marginBottom: 2, padding: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 1,
              }}
            >
              <Typography variant="h6">{t('donation.stepAmount')}</Typography>
              <Button size="small" onClick={() => setCurrentStep(0)}>
                {t('common.edit')}
              </Button>
            </Box>
            <Divider />
            <Box sx={{ marginTop: 2 }}>
              <Typography>
                <strong>{t('donation.amount')}:</strong>{' '}
                {formatCurrency(donationAmount, i18n.language, true)}
              </Typography>
              <Typography>
                <strong>{t('donation.displayName')}:</strong>{' '}
                {formData.isAnonymous ? t('common.anonymous') : formData.username}
              </Typography>
              {formData.message && (
                // Donor messages are free-form: a single unbroken 500-char "word"
                // must wrap rather than widen the layout.
                <Typography sx={{ overflowWrap: 'anywhere' }}>
                  <strong>{t('donation.message')}:</strong> {formData.message}
                </Typography>
              )}
            </Box>
          </Paper>

          {hasBidwarSelection && (
            <Paper sx={{ marginBottom: 2, padding: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 1,
                }}
              >
                <Typography variant="h6">{t('donation.stepBidwars')}</Typography>
                <Button size="small" onClick={() => setCurrentStep(1)}>
                  {t('common.edit')}
                </Button>
              </Box>
              <Divider />
              <Box sx={{ marginTop: 2 }}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  selectedBidwars.map(item => (
                    <Box key={item.id}>
                      <Typography>
                        <strong>{item.bidwarLabel}:</strong> {item.optionLabel}
                      </Typography>
                      {item.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: 'italic', whiteSpace: 'pre-line' }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          )}

          <Paper sx={{ marginBottom: 2, padding: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 1,
              }}
            >
              <Typography variant="h6">{t('donation.stepReceipt')}</Typography>
              <Button size="small" onClick={() => setCurrentStep(2)}>
                {t('common.edit')}
              </Button>
            </Box>
            <Divider />
            <Box sx={{ marginTop: 2 }}>
              <Typography>
                <strong>{t('donation.fullName')}:</strong> {formData.userFullName}
              </Typography>
              {formData.userCompany && (
                <Typography>
                  <strong>{t('donation.companyName')}:</strong> {formData.userCompany}
                </Typography>
              )}
              <Typography>
                <strong>{t('donation.email')}:</strong> {formData.userEmail}
              </Typography>
              <Typography>
                <strong>{t('donation.address')}:</strong>{' '}
                {`${formData.userAddress}, ${formData.userCity}, ${formData.userProvince}, ${formData.userPostalCode}, ${formData.userCountry}`}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Right side: Preview */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography gutterBottom variant="h6">
            {t('donation.preview')}
          </Typography>
          <DonationCard donation={donationData} />
        </Grid>
      </Grid>

      <Paper sx={{ marginBottom: 2, padding: 2 }}>
        <FormControlLabel
          sx={{ alignItems: 'center', gap: 1 }}
          label={t('donation.saveMyInfo')}
          control={
            <Checkbox
              name="saveMyInfo"
              checked={shouldSaveInfo}
              onChange={event => setShouldSaveInfo(event.target.checked)}
            />
          }
        />
        <Typography variant="caption" color="text.secondary" component="p">
          {t('donation.saveMyInfo.helper')}
        </Typography>

        <Divider sx={{ marginY: 2 }} />

        <FormControlLabel
          sx={{ alignItems: 'center', gap: 1 }}
          control={
            <Checkbox
              required
              name="agreeToTerms"
              checked={acceptedTerms}
              onChange={event => setAcceptedTerms(event.target.checked)}
            />
          }
          label={
            <Trans
              i18nKey="donation.agreeToTerms"
              components={{
                terms: (
                  <MuiLink
                    href="/policies/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={event => event.stopPropagation()}
                  />
                ),
                refund: (
                  <MuiLink
                    href="/policies/refund"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={event => event.stopPropagation()}
                  />
                ),
              }}
            />
          }
        />
      </Paper>

      <Box mt={4} sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={prevStep}>
          {t('common.back')}
        </Button>
        <Button variant="contained" onClick={nextStep} disabled={!acceptedTerms}>
          {t('common.confirmAndPay')}
        </Button>
      </Box>
    </Box>
  );
}

const StepReviewPropTypes = {
  formData: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    username: PropTypes.string,
    message: PropTypes.string,
    isAnonymous: PropTypes.bool,
    userFullName: PropTypes.string,
    userCompany: PropTypes.string,
    userEmail: PropTypes.string,
    userAddress: PropTypes.string,
    userCity: PropTypes.string,
    userProvince: PropTypes.string,
    userPostalCode: PropTypes.string,
    userCountry: PropTypes.string,
    bidwarChoices: PropTypes.object,
  }).isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  setShouldSaveInfo: PropTypes.func.isRequired,
  shouldSaveInfo: PropTypes.bool.isRequired,
};
StepReview.propTypes = StepReviewPropTypes;
